import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { LoginPage } from "@/pages/auth/login";
import { DashboardPage } from "@/pages/main";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/shadcn/toaster";
import { AcceptQuotePage } from "@/pages/client/accept-quote";
import ClientsDashboard from "@/pages/clients";
import AssetsDashboard from "@/pages/assets";
import UsersDashboard from "@/pages/users";
import VirtualMarketsDashboard from "@/pages/virtual-markets";

// Create Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Staging Environment Banner
const StagingBanner = () => {
  const env = import.meta.env.VITE_ENV;
  if (env !== "staging" && env !== "dev") return null;

  return (
    <>
      <div className="fixed top-0 left-0 right-0 bg-orange-100 border-b-4 border-orange-500 text-orange-800 text-center py-2 font-bold z-[9999]">
        * WARNING: This is a {env} deployment *
      </div>
      <div className="pt-11" />
    </>
  );
};

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    // TODO: Add a loading spinner
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
}

// Admin Route Component
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (user?.role !== "admin") {
    return <Navigate to="/" />;
  }

  return <>{children}</>;
}

// Public Route Component
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isAuthenticated) {
    return <Navigate to="/" />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <div className="min-h-screen">
            <StagingBanner />
            <Routes>
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <LoginPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/client/accept-quote/:slug"
                element={<AcceptQuotePage />}
              />
              <Route
                path="/clients"
                element={
                  <ProtectedRoute>
                    <ClientsDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/assets"
                element={
                  <ProtectedRoute>
                    <AssetsDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/virtual-markets"
                element={
                  <ProtectedRoute>
                    <VirtualMarketsDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/users"
                element={
                  <AdminRoute>
                    <UsersDashboard />
                  </AdminRoute>
                }
              />
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </div>
          <Toaster />
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
